import { useConfigContext } from '../contexts/configContext';
import { BannersConfig, CommonPaymentBannerTrackingAttributes } from '../types';
import { useApi } from '../api';
import { useEffect, useState } from 'preact/hooks';
import { cx } from '../utils/classnames';
import { FRICTIONLESS_PAYMENT_PATH } from './constants';

type UpdateAction = ({ type: 'email' } | { type: 'link'; url: string }) & {
  trackingType: CommonPaymentBannerTrackingAttributes['actionType'];
};

// export for use by PaymentAlert to populate the tracking payload
export function getUpdateAction(config: BannersConfig): UpdateAction | null {
  if (config.platform === 'shopify' && config.shopifySettings) {
    const { customerAccountsVersion, shopId, frictionlessPaymentsEnabled, myShopifyUrl } = config.shopifySettings;
    if (frictionlessPaymentsEnabled && myShopifyUrl) {
      return {
        type: 'link',
        url: `https://${myShopifyUrl}/${FRICTIONLESS_PAYMENT_PATH}?source=banner`,
        trackingType: 'frictionless_payment'
      };
    }
    if (customerAccountsVersion === 'new_customer_accounts') {
      return {
        type: 'link',
        url: `https://shopify.com/${shopId}/account/profile`,
        trackingType: 'new_accounts_payment'
      };
    }
    return { type: 'email', trackingType: 'email' };
  }

  if (config.externalPayment.enabled && config.externalPayment.url) {
    return { type: 'link', url: config.externalPayment.url, trackingType: 'external_url' };
  }

  return null;
}

export function UpdatePaymentButton({
  paymentId,
  onClick,
  onEmailSuccess,
  onEmailError
}: {
  paymentId: string;
  /** Called when the button is clicked, before any actions are taken (e.g. navigations or API calls) */
  onClick?: () => void | Promise<void>;
  onEmailSuccess?: () => void;
  onEmailError?: () => void;
}) {
  const config = useConfigContext();
  const { getPayment, sendPaymentEmail } = useApi();
  const [buttonState, setButtonState] = useState<'idle' | 'sending'>('idle');

  // a page restored from the back/forward cache keeps its JS state, so a click that navigated
  // away would otherwise come back still pending and refuse any further clicks
  useEffect(() => {
    // this intentionally does not check event.persisted since Safari's implementation can be buggy
    // it's okay to do this unconditionally since `idle` is the initial state anyway
    const reset = () => setButtonState('idle');
    window.addEventListener('pageshow', reset);
    return () => window.removeEventListener('pageshow', reset);
  }, []);

  const updateAction = getUpdateAction(config);

  if (!updateAction) return null;

  const handleClick = async () => {
    if (buttonState !== 'idle') return;
    // must be set before the tracking call below, so repeat clicks can't re-enter while work is in flight
    setButtonState('sending');

    const clickHandled = onClick?.();

    switch (updateAction.type) {
      case 'link':
        // we're firing the tracking call with `keepalive` so that it can complete even if the user navigates away
        // just in case (browser support/other unexpected issues), we also give it a head start before navigating away
        // UX research indicates that a 100ms repsonse time still feels instantaneous to users, so this should be a safe buffer
        await Promise.race([clickHandled, delay(80)]);
        window.location.href = updateAction.url;
        return;
      case 'email': {
        try {
          const { token_id } = await getPayment(paymentId);
          await sendPaymentEmail(token_id);
          onEmailSuccess?.();
        } catch (e) {
          console.error('Failed to send update payment email', e);
          onEmailError?.();
        } finally {
          setButtonState('idle');
        }
      }
    }
  };

  return (
    <button
      class={cx('payment-update-button', buttonState === 'sending' && 'payment-update-button--loading')}
      onClick={handleClick}
    >
      Update payment method
    </button>
  );
}

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
